import React, { forwardRef, useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { useCombinedRef } from '../../hooks/useCombinedRef';
import { useEmojiLengthLimit } from '../../hooks/useEmojiLengthLimit';
import Icon from '../Icon';
import Typography from '../Typography';
import type { InputBaseProps } from './index.d';
import {
    BorderCutWrapper,
    ClearButton,
    Counter,
    Input,
    InputWrapper,
    Label,
    LoadingIcon,
    StaticLabel,
    Textarea,
    Tip,
    TipWrapper,
} from './view';

export interface TextFieldProps extends InputBaseProps {
    index?: number;
    label?: string;
    errorText?: string;
    successText?: string;
    processingText?: string;
    actionState?: 'processing' | 'success' | 'error';
    maxLength?: number;
    width?: string;
    height?: string;
    prefix?: string;
    removeOuterWith?: boolean;
    withStaticLabel?: boolean;
    isMultiline?: boolean;
    showClear?: boolean;
    hideClearWhenEmpty?: boolean;
    hideLimit?: boolean;
    onChange?: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, index?: number) => void;
    onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>, index?: number) => void;
    handleClear?: (index?: number) => void;
    handleBlur?: (index?: number) => void;
    handleFocus?: (index?: number) => void;
    name?: string;
    captionAlign?: string;
    autoFocus?: boolean;
    cy?: string;
    cyPrefix?: string;
    value?: string;
    counterValue?: string;
    allowSubmitOnEnter?: boolean;
    autoComplete?: string;
}

const TextFieldComponent = forwardRef<HTMLTextAreaElement | HTMLInputElement, TextFieldProps>((props, forwardedRef) => {
    const {
        index = 0,
        label,
        actionState,
        errorText,
        successText,
        processingText,
        children,
        className,
        disabled = false,
        type,
        inputRef, // TODO убрать этот реф и использовать только реактовский forwardRef
        width,
        removeOuterWith,
        prefix,
        withStaticLabel,
        placeholder,
        onChange,
        onPaste,
        inputMode,
        value = '',
        isMultiline,
        showClear,
        hideClearWhenEmpty,
        hideLimit,
        handleClear,
        handleBlur,
        handleFocus,
        error,
        height,
        name,
        captionAlign = '',
        autoFocus = false,
        cy = '',
        cyPrefix,
        counterValue,
        maxLength,
        allowSubmitOnEnter = false,
        autoComplete = 'on',
        ...other
    } = props;

    const [isFocused, setIsFocused] = useState(autoFocus);
    const isLabelEventBypass = useRef(false);

    const isError = actionState === 'error' || !!error;
    const isSuccess = actionState === 'success';
    const isProcessing = actionState === 'processing';
    const noAction = !isError && !isSuccess && !isProcessing;
    const showProcessingText = isProcessing && !isError && !isSuccess;
    const showErrorText = isError && !isSuccess && !isProcessing;
    const showSuccessText = isSuccess && !isError && !isProcessing;

    const handleInputFocus = useCallback(() => {
        setIsFocused(true);
        handleFocus?.();
    }, [handleFocus]);

    const handleInputBlur = useCallback(() => {
        setIsFocused(false);
        handleBlur && handleBlur(index);
    }, [index, handleBlur]);

    const { resolvedCounterValue, handleChange: handleInputChange } = useEmojiLengthLimit({
        value,
        maxLength,
        counterValue,
        onChange,
        index,
    });
    const handleInputClear = useCallback(
        (event: React.SyntheticEvent) => {
            if (isLabelEventBypass.current) {
                isLabelEventBypass.current = false;

                return;
            }
            event?.preventDefault();
            handleClear && handleClear(index);
        },
        [index, handleClear],
    );

    const combinedRef = useCombinedRef(forwardedRef, inputRef);

    const labelEventTrap = (event: React.KeyboardEvent) => {
        if (allowSubmitOnEnter && event?.code === 'Enter') {
            isLabelEventBypass.current = true;
        }
    };

    const showStaticLabel = Boolean(withStaticLabel && (isFocused || value || prefix));
    const id = `${index}${name}`;
    const isClearButtonShown = hideClearWhenEmpty ? showClear && !!value : showClear;
    const dataCyTextArea = cy ? cy : cyPrefix ? `${cyPrefix}TextArea` : undefined;
    const dataCyTextInput = cy ? cy : cyPrefix ? `${cyPrefix}Input` : undefined;

    return (
        <Label
            htmlFor={id}
            width={width}
            className={className}
            removeOuterWith={removeOuterWith}
            onKeyUp={labelEventTrap}
            onKeyDown={labelEventTrap}
            onKeyPress={labelEventTrap}
        >
            <BorderCutWrapper data-border-cut-wrapper>
                <InputWrapper
                    data-input-wrapper
                    isFocused={isFocused}
                    data-error={isError}
                    isError={isError}
                    isMultiline={isMultiline}
                    withStaticLabel={showStaticLabel}
                    disabled={disabled}
                    height={height}
                >
                    {prefix && (
                        <Typography
                            variant="body4"
                            colorVariant="second"
                        >
                            {prefix}
                        </Typography>
                    )}
                    {showStaticLabel && (
                        <StaticLabel
                            disabled={disabled}
                            isError={isError}
                            isActive={isFocused}
                            variant="caption1"
                        >
                            {placeholder}
                        </StaticLabel>
                    )}
                    {isMultiline ? (
                        <Textarea
                            id={id}
                            withPrefix={prefix}
                            name={name}
                            data-error={isError}
                            isError={isError}
                            data-cy={dataCyTextArea}
                            data-autofocus={autoFocus}
                            onFocus={handleInputFocus}
                            ref={combinedRef}
                            value={value}
                            rows={1}
                            placeholder={
                                (!isFocused && withStaticLabel && !prefix) || !withStaticLabel ? placeholder : ''
                            }
                            disabled={disabled}
                            $showClear={isClearButtonShown}
                            onChange={handleInputChange}
                            {...other}
                            autoFocus={autoFocus}
                            onBlur={handleInputBlur}
                            onPaste={onPaste}
                        />
                    ) : (
                        <Input
                            id={id}
                            withPrefix={prefix}
                            name={name}
                            data-error={isError}
                            data-cy={dataCyTextInput}
                            data-autofocus={autoFocus}
                            isError={isError}
                            onFocus={handleInputFocus}
                            ref={combinedRef}
                            value={value}
                            placeholder={
                                (!isFocused && withStaticLabel && !prefix) || !withStaticLabel ? placeholder : ''
                            }
                            disabled={disabled}
                            type={type}
                            inputMode={inputMode}
                            onChange={handleInputChange}
                            autoFocus={autoFocus}
                            autoComplete={autoComplete}
                            {...other}
                            onBlur={handleInputBlur}
                            onPaste={onPaste}
                        />
                    )}
                    {isClearButtonShown && (
                        <ClearButton
                            onClick={handleInputClear}
                            data-cy={cyPrefix ? `${cyPrefix}ClearButton` : undefined}
                        >
                            <Icon
                                icon="icClose16"
                                size={16}
                                colorVariant="third"
                            />
                        </ClearButton>
                    )}
                    <>{children}</>
                </InputWrapper>
            </BorderCutWrapper>
            <TipWrapper>
                {(isError || isSuccess || isProcessing || label) && (
                    <Tip
                        variant="caption1"
                        isMultiline={isMultiline}
                        block
                        isError={isError}
                        disabled={disabled}
                        isSuccess={isSuccess}
                        textAlign={captionAlign}
                    >
                        {noAction && label}
                        {showErrorText && (errorText || label)}
                        {showSuccessText && (successText || label)}
                        {showProcessingText && (processingText || label)}
                        {showProcessingText && <LoadingIcon icon="icSpinner" />}
                    </Tip>
                )}
                {resolvedCounterValue && !hideLimit ? (
                    <Counter variant="caption2">{resolvedCounterValue}</Counter>
                ) : null}
            </TipWrapper>
        </Label>
    );
});

export const TextField = styled(TextFieldComponent)``;

TextField.displayName = 'TextField';
